import { useEffect, useMemo, useState } from "react";
import {
  areOrganizationDatasetsLoaded,
  getDatasetKeysForOrganizationId,
  getLoadedOrganizationsForDatasetKeys,
  loadOrganizationDatasets,
} from "../data/organizationData";

function toError(error: unknown) {
  return error instanceof Error ? error : new Error(String(error));
}

function useOrganizationDataset(organizationId: string) {
  const [loadError, setLoadError] = useState<Error | null>(null);
  const datasetKeys = useMemo(
    () => getDatasetKeysForOrganizationId(organizationId),
    [organizationId],
  );
  const [organizations, setOrganizations] = useState(() =>
    getLoadedOrganizationsForDatasetKeys(datasetKeys),
  );
  const isLoading = !areOrganizationDatasetsLoaded(datasetKeys);

  useEffect(() => {
    let cancelled = false;
    setLoadError(null);
    setOrganizations(getLoadedOrganizationsForDatasetKeys(datasetKeys));
    loadOrganizationDatasets(datasetKeys)
      .then(() => {
        if (!cancelled) {
          setOrganizations(getLoadedOrganizationsForDatasetKeys(datasetKeys));
        }
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setLoadError(toError(error));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [datasetKeys]);

  return { organizations, isLoading, loadError };
}

/**
 * Get a single organization by ID.
 * Reads from the local JSON data, loading international country chunks only
 * when the organization ID needs them.
 */
export function useOrganization(organizationId: string) {
  const { organizations, isLoading, loadError } =
    useOrganizationDataset(organizationId);
  const organization = useMemo(
    () => organizations.find((o) => o.id === organizationId) ?? null,
    [organizations, organizationId],
  );

  return {
    data: organization,
    isLoading,
    error:
      loadError ??
      (!isLoading && !organization
        ? new Error("Organization not found")
        : null),
  };
}

/**
 * Get other locations in the same network (e.g. other Brown Fertility offices).
 * Returns empty array if org has no network.
 */
export function useNetworkSiblings(
  organizationId: string,
  networkName?: string,
) {
  const { organizations } = useOrganizationDataset(organizationId);
  return useMemo(() => {
    if (!networkName) return [];
    return organizations.filter(
      (o) => o.network?.name === networkName && o.id !== organizationId,
    );
  }, [organizations, organizationId, networkName]);
}
